import Popper from 'popper.js';
import {
  KatalComponent,
  coerceToBoolean,
  nextUniqueId,
} from '../../shared/base';
import {
  getFirstMatchingParent,
  createElement,
  prependChild,
} from '../../shared/utils';

/**
 * @component {kat-popover} KatalPopover This component provides a container for displaying complex custom content anchored to something on the page.
 * @contentProvided This is to let users know the components expects in inner html to be completed
 * @example ClickButtonTrigger {
 * "kat-aria-behavior": "tooltip",
 * "content": "
 *   <kat-popover-trigger position=\"bottom\" type=\"click\">
 *     <kat-button variant=\"primary\" label=\"Click me!\"></kat-button>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * "}
 * @example ClickCustomTrigger {
 * "kat-aria-behavior": "tooltip",
 * "content": "
 *   <kat-popover-trigger type=\"click\">
 *     <button class=\"kat-no-style\">
 *       <span>Click here to trigger a popover</span>
 *       <kat-popover-anchor position=\"bottom\">
 *         <kat-icon name=\"arrow_drop_down\" size=\"small\"></kat-icon>
 *       </kat-popover-anchor>
 *     </button>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * "}
 * @example FocusTrigger {
 * "kat-aria-behavior": "tooltip",
 * "content": "
 *   <kat-popover-trigger position=\"right\" type=\"focus\">
 *     <kat-input type=\"text\" katal-1-2-spacing-fixes placeholder=\"Focus this to trigger popover\"></kat-input>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * "}
 * @example Error {
 * "kat-aria-behavior": "tooltip",
 * "variant": "error",
 * "content": "
 *   <kat-popover-trigger position=\"bottom\" type=\"click\">
 *     <kat-button variant=\"primary\" label=\"Click me!\"></kat-button>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * "}
 * @example Complex {
 * "kat-aria-behavior": "interactive",
 * "content": "
 *   <kat-popover-trigger position=\"right\" type=\"click\">
 *     <kat-button label=\"Click me to show popover\"></kat-button>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover with complex content.</span>
 *     <kat-input type=\"text\" label=\"Form input\" constraint-label=\"More text\"></kat-input>
 *   </kat-popover-content>
 * "}
 * @example ProgrammaticTrigger {"content": "
 *   <kat-button id=\"button-trigger\" label=\"Click here to trigger popover\"></kat-button>
 *   <p id=\"example-anchor\">Lorem ipsum dolor sit amet, sit nihil utamur ne, alia persequeris sit id, usu ut erant quando.</p>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * ", "script": "
 *   document.querySelector(\"kat-button#button-trigger\").addEventListener(\"click\", function () {
 *     var popover = document.querySelector(\"#preview kat-popover\");
 *     var paragraph = document.querySelector(\"#example-anchor\");
 *     popover.displayAnchoredTo(paragraph, \"bottom\");
 *   });
 * "}
 * @example HoverButtonTrigger {
 * "kat-aria-behavior": "tooltip",
 * "content": "
 *   <kat-popover-trigger position=\"right\" type=\"hover\">
 *     <kat-button variant=\"primary\" label=\"Hover me!\"></kat-button>
 *   </kat-popover-trigger>
 *   <kat-popover-content>
 *     <span>This is an example popover triggered inline.</span>
 *   </kat-popover-content>
 * "}
 * @subcomponent ./popover-trigger.js
 * @subcomponent ./popover-anchor.js
 * @subcomponent ./popover-content.js
 * @subcomponent ./popover-hide.js
 * @event show Fired whenever the popover becomes visible. It contains the anchor and position properties which, respectively, are the
 * element the popover is anchored to and the desired position around the achor the popover should display.
 * @event hide Fired whenever the popover is hidden.
 * @guideline Do Popover trigger should only include one interactable element to trigger the popover.
 * @guideline Dont Popovers triggered by focus should not contain any interactable elements.
 * @guideline Dont Popovers should not be triggered by a single icon.
 * @status Production
 * @themes flo
 */
class KatalPopover extends KatalComponent {
  static get observedAttributes() {
    return ['visible', 'hide-behavior', 'variant', 'kat-aria-behavior'];
  }

  constructor() {
    super(KatalPopover.observedAttributes);

    this.lastAnchor = null;
    this.lastTrigger = null;

    this.onChildrenChanged = this.onChildrenChanged.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // We check if the popover was opened between the capture and bubble phase of a particular event.
    document.addEventListener(
      'click',
      evt => {
        this._wasOpenOnCapture = this.visible;
      },
      true
    ); // Note: This parameter make this click handler happen in the capture phase.

    document.addEventListener('click', evt => {
      if (this.hideBehavior !== 'click') return;

      // In other words we only hide the popover if it was open before this click happened, it's still open,
      // and the click was not on the trigger or popover itself. Without this the popover could close immediately
      // after it's opened.
      if (
        this._wasOpenOnCapture &&
        this.visible &&
        this.isClickOutside(evt.target)
      ) {
        this.hide();
      }
    });

    // We must watch animations on the popover to toggle this did-fade-in class. Without it we can't differieniate between the popover initial state and
    // when it's fading out.
    this.addEventListener('animationstart', evt => {
      // Note: Animation is defined in css
      if (
        evt.target.tagName === 'KAT-POPOVER-CONTENT' &&
        evt.animationName === 'fadeIn'
      ) {
        const content = this.getContent();
        content.classList.add('did-fade-in');
      }
    });
    this.addEventListener('animationend', evt => {
      // Note: Animation is defined in css
      if (
        evt.target.tagName === 'KAT-POPOVER-CONTENT' &&
        evt.animationName === 'fadeOut'
      ) {
        const content = this.getContent();
        content.classList.remove('did-fade-in');
      }
    });

    this._observer = new MutationObserver(this.onChildrenChanged);
    this._observer.observe(this, { childList: true });

    this.render();
  }

  /**
   * @classprop {boolean} visible Determines if the popover is currently visible. If set to true manually (instead of using either of the display methods) it
   * might display incorrectly as the popover requires either a trigger or anchor to display correctly. It is recommended to avoid using this attribute
   * directly unless you've defined a <kat-popover-trigger> or <kat-popover-anchor> element inside the popover or an anchor pointing to this popover.
   */
  get visible() {
    return this.getBooleanAttribute('visible');
  }

  set visible(value) {
    this.setBooleanAttribute('visible', value);
  }

  /**
   * @classprop {enum} hide-behavior Determines how the user can hide the popover. Defaults to "click".
   * @enum {value} click Clicks outside the popover or its trigger will cause the popover to hide.
   * @enum {value} manual Popover will only hide when its trigger is clicked or unfocused, or the hide() method is called.
   */
  get hideBehavior() {
    return this.getAttributeOrDefault('hide-behavior', 'click');
  }

  set hideBehavior(value) {
    this.setAttribute('hide-behavior', value);
  }

  /**
   * @classprop {enum} variant Determines the visual style of the popover. At present only affects the background and border color.
   * @enum {value} info Used for a standard neutral informational popover.
   * @enum {value} success Used to represent a successful state.
   * @enum {value} error Used to represent an error or invalid state.
   * @enum {value} alert Used to represent a warning.
   * @enum {value} white Non-semantic style with a white background and blue border.
   * @enum {value} snow Non-semantic style with an off-white background and a grey border.
   * @enum {value} azure Non-semantic style with an off-white background and a blue border.
   * @enum {value} jungle-mist Non-semantic style with a green background and no border.
   * @enum {value} zircon Non-semantic style with a grey background and no border.
   */
  get variant() {
    return this.getAttributeOrDefault('variant', 'info');
  }

  set variant(value) {
    this.setAttribute('variant', value);
  }

  /**
   * @classprop {enum} kat-aria-behavior Defines how accessibility controls are designed for the popover and associated trigger. Defaults to "interactive".
   * Use "interactive" if your popover has interactable controls. This option will automatically create an invisible close button for screen readers and will
   * associate the popover with triggers that control it. Use "tooltip" if your popover has no interactable controls. This option will associate the popover
   * to the trigger and adds settings that should make screen readers announce the contents of the popover when displayed. Use "none" when your use-case for
   * the popover and trigger aren't covered by the other options. If you choose this option you should be careful to implement accessibility controls
   * correctly.
   * @enum {value} interactive
   * @enum {value} tooltip
   * @enum {value} none
   */
  get katAriaBehavior() {
    return this.getAttributeOrDefault('kat-aria-behavior', 'interactive');
  }

  set katAriaBehavior(value) {
    this.setAttribute('kat-aria-behavior', value);
  }

  getAllAssociatedElements(tagName) {
    let anchors = [];
    const nestedAnchor = this.getElementsByTagName(tagName)[0];
    if (nestedAnchor) {
      anchors.push(nestedAnchor);
    }

    const id = this.getAttribute('id');
    if (id) {
      anchors = anchors.concat(
        Array.from(document.querySelectorAll(`${tagName}[for="${id}"]`))
      );
    }

    return anchors;
  }

  /**
   * Convenience API method to provide an easy way to keep the anchor defined in HTML but display the popover programmatically.
   * Will be invoked automatically if the visible attribute is set from false to true. It uses the position specified of the first anchor
   * found within the <kat-popover> element or first anchor that points to this popover with the "for" attribute.
   * @method
   */
  displayUsingFirstAnchor() {
    const anchors = this.getAllAssociatedElements('kat-popover-anchor');
    const triggers = this.getAllAssociatedElements('kat-popover-trigger');
    if (anchors.length > 0) {
      this.displayAnchoredTo(anchors[0], anchors[0].position);
    } else if (triggers.length > 0) {
      this.displayAnchoredTo(triggers[0], triggers[0].position);
    }
  }

  /**
   * Programmatically display the popover anchored to a given element in particular position. Does nothing if the popover is already
   * visible.
   * @method
   * @param {HTMLElement} element The element to anchor to.
   * @param {string} position The position where the popover tries to anchor onto the given element. Will fallback to another position
   * if the popover would have been clipped by the window at this position. Can be one of following: "top", "right", "bottom", "left"
   * @katalignore
   */
  displayAnchoredTo(element, position) {
    if (
      !(element instanceof HTMLElement) ||
      (this.visible && element === this.lastAnchor)
    )
      return;

    const content = this.getContent();
    if (!content) return;

    // Anchors rely on this as well to know if they are the current anchor
    this.lastAnchor = element;
    this.visible = true;

    this._popperInst = new Popper(element, content, {
      placement: position,
      modifiers: {
        arrow: {
          element: '.kat-popover-arrow',
        },
      },
    });

    this.dispatchCustomEvent('show', {
      anchor: element,
      position: position,
    });

    // https://sim.amazon.com/issues/SPCOMPONENTS-144
    // TODO: this feels like a hack and should be revisited
    this._popperInst.scheduleUpdate();
  }

  displayTriggeredBy(trigger) {
    if (
      !(trigger instanceof HTMLElement) ||
      (this.visible && trigger === this.lastTrigger)
    )
      return;

    const content = this.getContent();
    if (!content) return;

    this.lastTrigger = trigger;

    // First, choose the anchor inside the triggering trigger
    const nestedAnchor = trigger.querySelector('kat-popover-anchor');
    if (nestedAnchor) {
      this.displayAnchoredTo(nestedAnchor, nestedAnchor.position);
      return;
    }

    // If no anchor in trigger, choose the first anchor in DOM order inside popover or outside the popover
    const anchors = this.getAllAssociatedElements('kat-popover-anchor');
    if (anchors.length > 0) {
      this.displayAnchoredTo(anchors[0], anchors[0].position);
      return;
    }

    // If no anchors at all, choose the triggering trigger
    this.displayAnchoredTo(trigger, trigger.position);
  }

  /**
   * Programmatically close the popover. Does nothing if the popover is not open.
   * @method
   */
  hide() {
    if (this.visible) {
      this.visible = false;
      // Note: tooltip behavior does nothing on hide
      if (this.katAriaBehavior === 'interactive') {
        this.ariaAnnounce('Popup closed');

        // Focus the trigger to help the user resume the same flow but only if focus was already in this popover
        if (
          this.lastTrigger &&
          this.lastTrigger.children.length > 0 &&
          document.activeElement
        ) {
          // activeElement is the currently focused element, only move focus if it was already inside the popover or it's trigger
          const matching = getFirstMatchingParent(
            document.activeElement,
            ele => ele === this.lastTrigger || ele === this
          );
          if (matching !== null) {
            this.lastTrigger.children[0].focus();
          }
        }
      }

      this.lastAnchor = null;
      this.lastTrigger = null;
      this.dispatchCustomEvent('hide');
    }
  }

  /**
   * Indicates if the given clicked element is outside of this popover and it's trigger.
   * Visits each of the element's parents and returns false if the popover or trigger is found.
   */
  isClickOutside(target) {
    const content = this.getContent();

    const matched = getFirstMatchingParent(
      target,
      ele => ele === this.lastTrigger || ele === content
    );
    return matched === null;
  }

  onChildrenChanged() {
    this.render();
  }

  ariaAnnounce(message) {
    if (!this.ariaAnnouncer) return;

    this.ariaAnnouncer.innerText = message;

    clearTimeout(this._ariaAnnouncerHideTimeout);
    this._ariaAnnouncerHideTimeout = setTimeout(() => {
      this.ariaAnnouncer.innerText = '';
    }, 2000);
  }

  render() {
    const content = this.getContent();
    if (content) {
      if (!content.hasAttribute('id')) {
        content.setAttribute('id', nextUniqueId());
      }
      this.updateContentAria();
    }

    if (!this.ariaAnnouncer) {
      this.ariaAnnouncer = createElement('div', {
        class: 'kat-visually-hidden',
        'aria-live': 'polite',
      });
      this.appendChild(this.ariaAnnouncer);
    }
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    const old = coerceToBoolean(oldValue);
    const newVal = coerceToBoolean(newValue);
    // Only call this display function if visible goes from false to true.
    if (attr === 'visible' && !old && newVal) {
      this.displayUsingFirstAnchor();
    }

    if (attr === 'kat-aria-behavior' && oldValue !== newValue) {
      this.updateContentAria();
      // Update all associated triggers
      this.getAllAssociatedElements('kat-popover-trigger').forEach(trigger => {
        if (trigger.updateAriaAttributes) {
          trigger.updateAriaAttributes();
        }
      });
    }
  }

  updateContentAria() {
    const content = this.getContent();
    if (!content) return;

    if (this.katAriaBehavior === 'tooltip') {
      content.setAttribute('role', 'tooltip');
      content.removeAttribute('tabindex');
    } else {
      content.removeAttribute('role');
    }

    if (this.katAriaBehavior === 'interactive') {
      content.setAttribute('tabindex', '0');
      if (!this.ariaCloseButton) {
        this.ariaCloseButton = createElement(
          'kat-popover-hide',
          { class: 'kat-popover-aria-close-button' },
          [
            createElement('button', {
              class: 'kat-no-style',
              tabindex: '-1',
              'aria-label': 'Inside popup, click this to close',
            }),
          ]
        );
      }

      if (
        !content.querySelector('kat-popover-hide.kat-popover-aria-close-button')
      ) {
        prependChild(content, this.ariaCloseButton);
      }
    } else {
      if (
        this.querySelector('kat-popover-hide.kat-popover-aria-close-button') ===
        this.ariaCloseButton
      ) {
        content.removeChild(this.ariaCloseButton);
      }
    }
  }

  getContent() {
    return this.getElementsByTagName('kat-popover-content')[0];
  }
}

KatalComponent.register('kat-popover', KatalPopover);
